import { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { AnnouncementMedia } from "@/components/AnnouncementMedia";
import { FilePdf, LockKey, CheckCircle, Eye } from "@phosphor-icons/react";
import { toast } from "sonner";

const stamp = (iso) => (iso ? new Date(iso).toLocaleString() : null);
const FILTERS = [
    ["all", "All"], ["not_presented", "Not seen"], ["presented", "Seen"],
    ["unlocked", "Unlocked"], ["acknowledged", "Acknowledged"],
];
const STATUS_BADGE = {
    not_presented: "bg-slate-100 text-slate-600 border-slate-200",
    presented: "bg-blue-50 text-[#0066D9] border-blue-200",
    unlocked: "bg-indigo-50 text-indigo-700 border-indigo-200",
    acknowledged: "bg-emerald-50 text-emerald-800 border-emerald-200",
};

const Bar = ({ label, value, total, icon: Icon, color }) => {
    const pct = total ? Math.round((value / total) * 100) : 0;
    return (
        <div data-testid={`tracking-bar-${label.toLowerCase().replace(/\s+/g, "-")}`}>
            <div className="flex items-center justify-between text-xs mb-1.5">
                <span className="flex items-center gap-1.5 text-slate-600 font-medium">
                    <Icon size={13} /> {label}
                </span>
                <span className="text-[#003366] font-semibold">
                    {value}/{total} · {pct}%
                </span>
            </div>
            <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                <div className={`h-full rounded-full ${color}`} style={{ width: `${pct}%` }} />
            </div>
        </div>
    );
};

/** Per-recipient delivery tracking + PDF report. */
export const AnnouncementTrackingDialog = ({ tracking, onClose }) => {
    const [filter, setFilter] = useState("all");
    const [q, setQ] = useState("");
    const [downloading, setDownloading] = useState(false);

    const a = tracking?.announcement;
    const s = tracking?.summary || {};
    const total = s.recipients || 0;

    const rows = (tracking?.recipients || []).filter((r) => {
        if (q.trim() && !r.user_name.toLowerCase().includes(q.trim().toLowerCase())) return false;
        if (filter === "all") return true;
        if (filter === "presented") return Boolean(r.presented_at);
        if (filter === "not_presented") return !r.presented_at;
        if (filter === "unlocked") return Boolean(r.unlocked_at);
        if (filter === "acknowledged") return Boolean(r.acknowledged_at);
        return true;
    });

    const downloadPdf = async () => {
        setDownloading(true);
        try {
            const { data } = await api.get(`/announcements/${a.id}/tracking/pdf`, { responseType: "blob" });
            const url = URL.createObjectURL(data);
            const link = document.createElement("a");
            link.href = url;
            link.download = `AdvanceNetworks_AnnouncementTracking.pdf`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            setTimeout(() => URL.revokeObjectURL(url), 4000);
            toast.success("PDF downloaded");
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setDownloading(false);
        }
    };

    return (
        <Dialog open={Boolean(tracking)} onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="tracking-dialog">
                <DialogHeader>
                    <DialogTitle style={{ fontFamily: "Outfit" }}>{a?.title}</DialogTitle>
                    <DialogDescription>
                        Who has seen, unlocked and acknowledged this notice.
                    </DialogDescription>
                </DialogHeader>
                {tracking && (
                    <div className="space-y-5">
                        <div className="flex flex-wrap items-center gap-2">
                            <Badge className="bg-slate-100 text-slate-700 border border-slate-200 capitalize">
                                {a.state}
                            </Badge>
                            <Badge className="bg-slate-100 text-slate-700 border border-slate-200 capitalize">
                                {a.priority}
                            </Badge>
                            {a.requires_pin && (
                                <Badge className="bg-slate-900 text-white border-slate-900">
                                    <LockKey size={11} className="mr-1" /> Private
                                </Badge>
                            )}
                            {a.requires_acknowledgment && (
                                <Badge className="bg-blue-50 text-[#0066D9] border border-blue-200">
                                    Acknowledgment required
                                </Badge>
                            )}
                            <Button size="sm" variant="outline" onClick={downloadPdf} disabled={downloading}
                                className="ml-auto border-[#003366] text-[#003366]"
                                data-testid="tracking-download-pdf">
                                <FilePdf size={14} className="mr-1.5" />
                                {downloading ? "Preparing…" : "Download PDF"}
                            </Button>
                        </div>

                        <div className="space-y-3 p-4 rounded-md border border-[#E2E8F0]" data-testid="tracking-summary">
                            <Bar label="Seen" value={s.presented || 0} total={total} icon={Eye} color="bg-[#0066D9]" />
                            {a.requires_pin && (
                                <Bar label="Unlocked" value={s.unlocked || 0} total={total} icon={LockKey}
                                    color="bg-indigo-500" />
                            )}
                            {a.requires_acknowledgment && (
                                <Bar label="Acknowledged" value={s.acknowledged || 0} total={total}
                                    icon={CheckCircle} color="bg-emerald-500" />
                            )}
                            <div className="text-xs text-slate-500 pt-1" data-testid="tracking-not-presented">
                                {s.not_presented || 0} of {total} recipient{total === 1 ? "" : "s"} have not seen it yet.
                            </div>
                        </div>

                        {a.media && <AnnouncementMedia announcementId={a.id} media={a.media} />}

                        <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
                            <div className="flex flex-wrap gap-1.5">
                                {FILTERS.map(([key, label]) => (
                                    <Button key={key} size="sm" variant={filter === key ? "default" : "outline"}
                                        className={filter === key ? "bg-[#003366] h-8" : "border-[#E2E8F0] text-slate-600 h-8"}
                                        onClick={() => setFilter(key)} data-testid={`tracking-filter-${key}`}>
                                        {label}
                                    </Button>
                                ))}
                            </div>
                            <Input placeholder="Find an employee…" value={q} onChange={(e) => setQ(e.target.value)}
                                className="h-9 sm:max-w-[200px] sm:ml-auto border-[#E2E8F0]"
                                data-testid="tracking-search-input" />
                        </div>

                        <div className="border border-[#E2E8F0] rounded-md divide-y divide-slate-100"
                            data-testid="tracking-recipient-list">
                            {rows.length === 0 ? (
                                <div className="px-4 py-6 text-center text-sm text-slate-500"
                                    data-testid="tracking-no-rows">
                                    No recipients match this filter.
                                </div>
                            ) : rows.map((r) => (
                                <div key={r.user_name} className="px-4 py-2.5 flex flex-wrap items-center gap-2 text-sm">
                                    <span className="flex-1 min-w-[140px] font-medium text-[#1F2A44]">{r.user_name}</span>
                                    <span className="text-xs text-slate-500">
                                        {stamp(r.presented_at) ? `Seen ${stamp(r.presented_at)}` : "Not seen yet"}
                                        {stamp(r.unlocked_at) ? ` · Unlocked ${stamp(r.unlocked_at)}` : ""}
                                        {stamp(r.acknowledged_at) ? ` · Acknowledged ${stamp(r.acknowledged_at)}` : ""}
                                    </span>
                                    <Badge className={`${STATUS_BADGE[r.status]} border text-[11px] capitalize`}>
                                        {r.status.replace("_", " ")}
                                    </Badge>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
};
